import React, { useContext, useEffect, useRef, useState } from 'react';
import Mic from '../../../../../../Assets/Lens/Mic.svg';
import MicRed from '../../../../../../Assets/Lens/MicRed.svg';
import { setEditSection, setVideo } from '../../../../../../Features/lensSlice';
import { useDispatch, useSelector } from 'react-redux';
import Undo from '../../../../../../Assets/Lens/Undo.svg';
import Redo from '../../../../../../Assets/Lens/Redo.svg';
import { extractFrame } from '../../../../../../lib/utils/utils';
import { VideoContext } from '../../../../../../Context/VideoContext';

import { fetchFile } from '@ffmpeg/util';
import { FFmpeg } from '@ffmpeg/ffmpeg';

import { showToast } from '../../../../../../Components/Toaster/Toaster';

//Store the ffmpeg instance
let ffmpeg;

function VoiceoverEdit() {
    const dispatch = useDispatch();
    const video = useSelector((state) => state.lens.media.video);

    const [duration, setDuration] = useState(0);
    const [isRecording, setIsRecording] = useState(false);
    const [backgroundImage, setBackgroundImage] = useState('');
    const [barPosition, setBarPosition] = useState(0);

    const [error, setError] = useState('');

    const [microphoneStatus, setMicrophoneStatus] = useState(false);

    const [temporaryUrl, setTemporaryUrl] = useState('');

    const sliderRef = useRef(null);
    const {
        videoFile,
        setVideoFile,
        setOriginalAudioUrl,
        loading,
        setLoading,
        setSelectedAudio,
        setHasVoiceOver
    } = useContext(VideoContext);

    const handleRecording = () => {
        if(microphoneStatus) {
            if(!loading) {
                setIsRecording(!isRecording);
                setError('');
            }
        }
        else {
            setError('Microphone is off or access denied');
        }
    }

    const handleUndo = () => {
        if(recordedUrl) {
            setTemporaryUrl(recordedUrl);
            setRecordedUrl('');
            setBarPosition(0);
            setError('');
        }
        else {
            setError('Theres no recording history.');
        }
    }

    const handleRedo = () => {
        if(temporaryUrl) {
            setRecordedUrl(temporaryUrl);
            setError('');
        }
        else {
            setError('Theres no recording history.');
        }
    }

    const handleCancel = () => {
        dispatch(setEditSection(null));
    }

    // Audio Recording Functions

    const mediaStream = useRef(null);
    const mediaRecorder = useRef(null);
    const chunks = useRef([]);
    const [recordedUrl, setRecordedUrl] = useState('');
    const [currentTime, setCurrentTime] = useState(0);

    // Timer bar movement while recording audio
    const [recordingTimer, setRecordingTimer] = useState(0);
    const intervalRef = useRef(null);

    useEffect(() => {
        const sliderElement = sliderRef.current;

        if (sliderElement) {
            const rect = sliderElement.getBoundingClientRect();
            const pixelsPerSecond = (rect.width - 3.65) / duration;

            if (recordingTimer > duration) {
                const timeValue = (recordingTimer - 1) + (duration % (recordingTimer - 1));
                setCurrentTime(parseInt(timeValue));
                const positionFromLeft = timeValue * pixelsPerSecond;
                setBarPosition(positionFromLeft);
                stopRecording();
            }
            else {
                setCurrentTime(parseInt(recordingTimer));
                const positionFromLeft = recordingTimer * pixelsPerSecond;
                setBarPosition(positionFromLeft);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordingTimer]);

    const startRecording = async () => {
        setRecordingTimer(0);
        try {
            // Timer for the current time bar to move when audio recording starts
            intervalRef.current = setInterval(() => {
                setRecordingTimer(prevTimer => prevTimer + 1);
            }, 1000);

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStream.current = stream;
            mediaRecorder.current = new MediaRecorder(stream);
            mediaRecorder.current.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunks.current.push(e.data);
                }
            };
            mediaRecorder.current.onstop = () => {
                const recordedBlob = new Blob(chunks.current, { type: 'audio/mp3' });
                const url = URL.createObjectURL(recordedBlob);
                setRecordedUrl(url);
                chunks.current = [];
            };
            mediaRecorder.current.start();
        }
        catch (error) {
            stopRecording();
            showToast(`Error accessing microphone: ${error}`, 'error');
        }
    };

    const stopRecording = () => {
        if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
            mediaRecorder.current.stop();
        }
        if (mediaStream.current) {
            mediaStream.current.getTracks().forEach((track) => {
                track.stop();
            });
        }
        clearInterval(intervalRef.current);
        setIsRecording(false);
    };

    // Recording Play Pause Handler
    useEffect(() => {
        if(isRecording) {
            const handleRecording = async () => {
                await startRecording();
            }

            handleRecording();
        }
        else {
            stopRecording();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRecording]);

    // Merge Audio

    const [isScriptLoaded, setIsScriptLoaded] = useState(false);

    const handleConfirmVoiceover = async () => {
        setLoading(true);

        if (isScriptLoaded) {
            if (!recordedUrl || !videoFile) {
                showToast('Both audio and video are required.', 'warning');
                return;
            }

            const audioData = await fetch(recordedUrl).then(r => r.blob());
            const videoData = await fetchFile(videoFile);

            await ffmpeg.writeFile('audio.mp3', await fetchFile(audioData));
            await ffmpeg.writeFile('video.mp4', videoData);

            // Extract existing audio from the video
            const extractedAudio = await ffmpeg.exec(['-i', 'video.mp4', '-q:a', '0', '-map', 'a', 'extracted_audio.mp3']);

            // Save the extracted audio
            if(!Boolean(extractedAudio)) {
                const extractedAudioData = await ffmpeg.readFile('extracted_audio.mp3');
                const extractedAudioBlob = new Blob([extractedAudioData.buffer], { type: 'audio/mp3' });
                const extractedAudioUrl = URL.createObjectURL(extractedAudioBlob);
                setOriginalAudioUrl(extractedAudioUrl);
            }

            // Remove existing audio from the video
            await ffmpeg.exec(['-i', 'video.mp4', '-c:v', 'copy', '-an', 'video_no_audio.mp4']);

            // Merge the new audio with the video without existing audio
            await ffmpeg.exec(['-i', 'video_no_audio.mp4', '-i', 'audio.mp3', '-c:v', 'copy', '-c:a', 'aac', '-strict', 'experimental', 'output.mp4'])
            .then(() => {
                setSelectedAudio({});
                setLoading(false);
            })
            .catch((error) => {
                console.error('Promise rejected:', error);
                setLoading(false);
            });

            const data = await ffmpeg.readFile('output.mp4');
            const mergedBlob = new Blob([data.buffer], { type: 'video/mp4' });
            const url = URL.createObjectURL(mergedBlob);

            // Create File from blob
            const fileName = 'output.mp4';
            const mergedFile = new File([mergedBlob], fileName, { type: 'video/mp4' });

            setVideoFile(mergedFile);
            dispatch(setVideo({
                id: video.id,
                url: url,
            }));

            dispatch(setEditSection(null));
            setHasVoiceOver(true);
            showToast('Voiceover added to video!', 'success');
        }
    }

    useEffect(() => {
        const loadffmpeg = async () => {
            ffmpeg = new FFmpeg();
            await ffmpeg.load({ log: true });
            setIsScriptLoaded(true);
        }

        loadffmpeg();
    }, []);

    // Get Video Current Duration
    useEffect(() => {
        if (video.url) {
            const videoElement = document.createElement('video');
            videoElement.src = video.url;
      
            const handleLoadedMetadata = () => {
                setDuration(videoElement.duration.toFixed(2));
            };
      
            videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
      
            return () => {
                videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
            };
        }
    }, [video]);

    // Get Video Frame for Slider
    useEffect(() => {
        if (video) {
            extractFrame(video.url, 1).then((frame) => {
                setBackgroundImage(frame);
            }).catch((err) => {
                console.error('Error extracting frame:', err);
            });
        }
    }, [video]);

    useEffect(() => {
        const checkMicrophone = async () => {
            try {
                // Request microphone access
                await navigator.mediaDevices.getUserMedia({ audio: true });
                setMicrophoneStatus(true);
            }
            catch (error) {
                setMicrophoneStatus(false);
            }
        };
    
        checkMicrophone();
    
        return () => {
          // Clean up media stream if needed
        };
      }, []);

    return (
        <div className='relative flex flex-col size-full p-4 bg-[#FFFFFF]'>
            <div className="flex items-center justify-between">
                <h1 className='font-medium text-[16px] leading-5 text-[#121212] select-none'>Voiceover</h1>

                <button 
                    onClick={handleCancel} 
                    className='underline font-medium text-[14px] leading-5 text-[#1055EB] select-none'
                >Cancel</button>
            </div>

            <div className="absolute bottom-32 px-4 left-0 w-full flex flex-col items-center gap-4">
                <p className='font-normal text-[14px] leading-[22px] text-[#808080] select-none'>
                    <span className='text-[#121212]'>00:{currentTime < 10 ? `0${currentTime}` : currentTime}</span> / 00:{duration < 10 ? `0${parseInt(duration)}` : parseInt(duration)}
                </p>

                <div
                    className="w-full relative h-[52px] rounded-[7px]" 
                    ref={sliderRef} 
                    style={{
                        backgroundImage: `url(${backgroundImage})`,
                        backgroundRepeat: 'repeat',
                        backgroundSize: 'contain'
                    }}
                >
                    <div 
                        style={{
                            left: `${barPosition}px`
                        }}
                        className="absolute w-[3.5px] bg-[#FFFFFF] h-[62px] transition-all delay-0 ease-linear rounded top-[-5px] z-10"
                    ></div>
                </div>

                <div className="size-20 flex items-center justify-center relative">
                    <button onClick={handleRecording} className={`outline-none size-[72px] flex items-center justify-center rounded-full border-4 ${isRecording ? 'border-primaryColor' : 'border-[#808080]'}`}>
                        <img src={isRecording ? MicRed : Mic} alt="" />
                    </button>

                    <div className="flex items-center gap-4 absolute top-1/2 -left-24 -translate-y-1/2">
                        <button onClick={handleUndo} className='size-8 flex items-center justify-center rounded-full bg-[#F5F5F5]'>
                            <img src={Undo} alt="" />
                        </button>

                        <button onClick={handleRedo} className='size-8 flex items-center justify-center rounded-full bg-[#F5F5F5]'>
                            <img src={Redo} alt="" />
                        </button>
                    </div>
                </div>
            </div>

            <div className="absolute bottom-[72px] px-4 left-0 w-full">
                <p className='font-normal text-[12px] leading-6 text-[#666666]'>{error}</p>
            </div>

            {
                recordedUrl.length > 0 && 
                <div className="absolute bottom-0 left-0 w-full px-4 py-3 bg-[#FFFFFF] border-t border-[#EAEAEA]">
                    <button onClick={handleConfirmVoiceover} className='w-full px-4 py-[6px] rounded-md bg-primaryColor hover:bg-primaryAccent font-semibold text-[14px] leading-[26px] text-buttonText'>Confirm Voiceover</button>
                </div>
            }
        </div>
    );
}

export default VoiceoverEdit;
